import {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetModal,
  BottomSheetView,
} from '@gorhom/bottom-sheet'
import { router } from 'expo-router'
import { useCallback, useEffect, useRef } from 'react'
import { View } from 'react-native'
import { StyleSheet, useUnistyles } from 'react-native-unistyles'

import OrganizationAvatar from '@/components/navigation/OrganizationAvatar'
import UniDivider from '@/components/unistyles/UniDivider'
import UniIcon from '@/components/unistyles/UniIcon'
import UniText from '@/components/unistyles/UniText'
import UniTouchableRipple from '@/components/unistyles/UniTouchableRipple'
import useWidescreenBottomSheetStyle from '@/hooks/useWidescreenBottomSheetStyle'
import { useAppStore } from '@/store/useAppStore'
import trpc from '@/utils/trpc'

const OrganizationSwitcherBottomSheet = () => {
  const bottomSheetModalRef = useRef<BottomSheetModal>(null)
  const { theme } = useUnistyles()
  const widescreenStyle = useWidescreenBottomSheetStyle()

  const currentOrganization = useAppStore.use.currentOrganization().organization
  const setCurrentOrganization = useAppStore.use.setCurrentOrganization()
  const isVisible = useAppStore.use.organizationSwitcherVisible()
  const hideOrganizationSwitcher = useAppStore.use.hideOrganizationSwitcher()

  const currentUserQuery = trpc.user.current.useQuery(
    {
      organizationId: currentOrganization?.id,
    },
    {
      enabled: !!currentOrganization?.id,
    }
  )

  const organizations = currentUserQuery.data?.organizations
    .map((org) => org.organization)
    .filter((org) => org.id !== currentOrganization?.id)

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        pressBehavior={'close'}
      />
    ),
    []
  )

  useEffect(() => {
    if (isVisible) {
      bottomSheetModalRef.current?.present()
    } else {
      bottomSheetModalRef.current?.dismiss()
    }
  }, [isVisible])

  const handleOrganizationSwitch = (org: any) => {
    if (currentOrganization?.id !== org.id) {
      setCurrentOrganization({
        organization: {
          id: org.id,
          adoptionContract: org.adoptionContract,
          logo: org.logo,
          qrCode: org.qrCode,
          name: org.name,
        },
      })
    }

    router.dismissTo(`/organization/${org.id}/dashboard`)
    hideOrganizationSwitcher()
  }

  const handleNewRescue = () => {
    router.push('/getting-started/setup')
    hideOrganizationSwitcher()
  }

  return (
    <BottomSheetModal
      backdropComponent={renderBackdrop}
      enableDismissOnClose={true}
      enableDynamicSizing
      handleIndicatorStyle={{
        backgroundColor: theme.colors.onBackground,
      }}
      handleStyle={{
        backgroundColor: theme.colors.background,
      }}
      index={0}
      onDismiss={hideOrganizationSwitcher}
      ref={bottomSheetModalRef}
      style={widescreenStyle}
    >
      <BottomSheetView
        style={{
          backgroundColor: theme.colors.background,
          paddingBottom: theme.tokens.spacing[4],
        }}
      >
        <View style={styles.container}>
          <UniText style={styles.title} variant="titleLarge">
            Switch Organization
          </UniText>

          <UniTouchableRipple
            accessibilityRole="button"
            borderless={true}
            onPress={() => hideOrganizationSwitcher()}
            style={[styles.organizationItem, styles.currentOrg]}
          >
            <View style={styles.organizationContent}>
              <OrganizationAvatar
                logoId={currentOrganization?.logo?.id}
                name={currentOrganization?.name || ''}
                organizationId={currentOrganization?.id || ''}
                size={40}
              />
              <View style={styles.orgInfo}>
                <UniText style={styles.orgName}>
                  {currentOrganization?.name}
                </UniText>
                <UniText style={styles.currentLabel} variant="bodySmall">
                  Current Organization
                </UniText>
              </View>
              <UniIcon color={theme.colors.primary} size={20} source="check" />
            </View>
          </UniTouchableRipple>

          {organizations && organizations.length > 0 && (
            <>
              <UniDivider style={styles.divider} />

              {organizations.map((org) => (
                <UniTouchableRipple
                  accessibilityRole="button"
                  borderless={true}
                  key={org.id}
                  onPress={() => handleOrganizationSwitch(org)}
                  style={styles.organizationItem}
                >
                  <View style={styles.organizationContent}>
                    <OrganizationAvatar
                      logoId={org.logo?.id}
                      name={org.name}
                      organizationId={org.id}
                      size={40}
                    />
                    <View style={styles.orgInfo}>
                      <UniText style={styles.orgName}>{org.name}</UniText>
                    </View>
                  </View>
                </UniTouchableRipple>
              ))}
            </>
          )}

          <UniDivider style={styles.divider} />

          {/* New Rescue Button */}
          <UniTouchableRipple
            accessibilityRole="button"
            borderless={true}
            onPress={handleNewRescue}
            style={styles.organizationItem}
            testID="create-organization-button"
          >
            <View style={styles.organizationContent}>
              <UniIcon
                color={theme.colors.primary}
                size={40}
                source="plus-circle-outline"
              />
              <View style={styles.orgInfo}>
                <UniText style={styles.orgName}>New Rescue</UniText>
                <UniText style={styles.subtitle} variant="bodySmall">
                  Create a new organization
                </UniText>
              </View>
            </View>
          </UniTouchableRipple>
        </View>
      </BottomSheetView>
    </BottomSheetModal>
  )
}

const styles = StyleSheet.create((theme) => ({
  container: {
    padding: theme.tokens.spacing[4],
  },
  currentLabel: {
    color: theme.colors.primary,
    marginTop: theme.tokens.spacing[1],
  },
  currentOrg: {
    backgroundColor: theme.colors.primaryContainer,
  },
  divider: {
    marginVertical: theme.tokens.spacing[2],
  },
  orgInfo: {
    flex: 1,
    marginLeft: theme.tokens.spacing[3],
  },
  orgName: {
    fontSize: 16,
    fontWeight: '500',
  },
  organizationContent: {
    alignItems: 'center',
    flexDirection: 'row',
    padding: theme.tokens.spacing[3],
  },
  organizationItem: {
    borderRadius: theme.tokens.containerBorderRadius,
    marginVertical: theme.tokens.spacing[1],
  },
  subtitle: {
    color: theme.colors.onSurfaceVariant,
    marginTop: theme.tokens.spacing[1],
  },
  title: {
    fontWeight: 'bold',
    marginBottom: theme.tokens.spacing[4],
    textAlign: 'center',
  },
}))

export default OrganizationSwitcherBottomSheet
